import { useState, useMemo } from "react";
import { cloudCategories, CloudCategory } from "@/mocks/cloudData";

interface CategoryTreeProps {
  selectedCategoryId: string | null;
  onSelect: (id: string | null) => void;
  searchKeyword: string;
}

interface TreeNode extends CloudCategory {
  children: TreeNode[];
}

function buildTree(cats: CloudCategory[]): TreeNode[] {
  const roots = cats.filter((c) => c.parentId === null);
  return roots.map((root) => ({
    ...root,
    children: cats
      .filter((c) => c.parentId === root.id)
      .map((child) => ({ ...child, children: [] })),
  }));
}

export default function CategoryTree({ selectedCategoryId, onSelect, searchKeyword }: CategoryTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(["cc-1", "cc-2"]));

  const tree = useMemo(() => buildTree(cloudCategories), []);

  const filteredTree = useMemo(() => {
    if (!searchKeyword.trim()) return tree;
    const kw = searchKeyword.toLowerCase();
    return tree
      .map((node) => {
        const matchedChildren = node.children.filter((c) =>
          c.name.toLowerCase().includes(kw)
        );
        const selfMatch = node.name.toLowerCase().includes(kw);
        if (selfMatch || matchedChildren.length > 0) {
          return { ...node, children: selfMatch ? node.children : matchedChildren };
        }
        return null;
      })
      .filter(Boolean) as TreeNode[];
  }, [tree, searchKeyword]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalCount = cloudCategories
    .filter((c) => c.parentId === null)
    .reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b border-gray-100">
        <button
          onClick={() => onSelect(null)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
            selectedCategoryId === null
              ? "bg-orange-50 text-orange-600 font-medium"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-apps-2-line text-sm" />
            </div>
            <span>全部分类</span>
          </div>
          <span className="text-xs text-gray-400">{totalCount.toLocaleString()}</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {filteredTree.map((node) => {
          const isExpanded = expandedIds.has(node.id) || !!searchKeyword;
          const isSelected = selectedCategoryId === node.id;

          return (
            <div key={node.id}>
              <div
                className={`flex items-center gap-1 px-3 py-1.5 mx-1 rounded-lg cursor-pointer transition-colors group ${
                  isSelected ? "bg-orange-50 text-orange-600" : "hover:bg-gray-50 text-gray-700"
                }`}
                onClick={() => {
                  onSelect(node.id);
                  toggleExpand(node.id);
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(node.id);
                  }}
                  className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
                >
                  <i className={`text-xs ${isExpanded ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}`} />
                </button>
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${node.icon} text-sm ${isSelected ? "text-orange-500" : "text-gray-400"}`} />
                </div>
                <span className={`flex-1 text-sm font-medium truncate ${isSelected ? "text-orange-600" : ""}`}>
                  {node.name}
                </span>
                <span className="text-xs text-gray-400 shrink-0">{node.count.toLocaleString()}</span>
              </div>

              {isExpanded && node.children.length > 0 && (
                <div className="ml-4">
                  {node.children.map((child) => {
                    const isChildSelected = selectedCategoryId === child.id;
                    return (
                      <div
                        key={child.id}
                        onClick={() => onSelect(child.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 mx-1 rounded-lg cursor-pointer transition-colors ${
                          isChildSelected
                            ? "bg-orange-50 text-orange-600"
                            : "hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className={`ri-corner-down-right-line text-xs ${isChildSelected ? "text-orange-400" : "text-gray-300"}`} />
                        </div>
                        <span className={`flex-1 text-sm truncate ${isChildSelected ? "font-medium" : ""}`}>
                          {child.name}
                        </span>
                        <span className="text-xs text-gray-400 shrink-0">{child.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
